import { useState } from "react";
import { PIE_COLORS } from "./formatters";

/**
 * Generic donut/pie chart primitive shared by ChannelPieChart and
 * SalesPieChart. Handles SVG geometry, slice paths, hover state, and the
 * legend framework. Callers supply:
 *
 *   - `data`     items with a numeric `value` used for slice sizing
 *   - `getKey`   returns a stable string key per item (for hover/selection)
 *   - `centerValue` / `centerLabel`  big number + subtitle in the donut hole
 *   - `renderLegendRow({ item, color, isHover, isSelected, isDim, ...})`
 *              — full control over each legend row's markup
 *   - `onSelect` optional click handler that toggles a slice selection
 *   - `selectedKey` externally-controlled selected key (for click-to-filter)
 *
 * This replaces two ~120-line hand-rolled donuts with one primitive that
 * the two consumers wrap in ~35 lines each.
 */
export default function InsightPie({
  title,
  subtitle,
  header,           // optional slot: appears next to the title (e.g. InfoTip)
  data,
  getKey,
  getValue,
  colors = PIE_COLORS,
  centerLabel,
  centerValue,
  emptyMessage = "Sin datos en el período seleccionado.",
  testId,
  onSelect,
  selectedKey,
  renderLegendRow,
  legendMaxHeight,  // px — enables vertical scroll if the legend is long
}) {
  const [hover, setHover] = useState(null);
  const items = data || [];
  const total = items.reduce((s, d) => s + (getValue(d) || 0), 0);

  if (items.length === 0 || total === 0) {
    return (
      <div className="surface p-6" data-testid={`${testId}-empty`}>
        <h3 className="font-display text-base font-extrabold mb-2 inline-flex items-center gap-1.5">
          {title}
          {header}
        </h3>
        <p className="text-xs text-gray-500">{emptyMessage}</p>
      </div>
    );
  }

  const size = 200, r = 80, cx = size / 2, cy = size / 2;

  // Build slice paths — items with value === 0 are kept in the legend but
  // don't render an arc (avoids invisible-hover zones).
  let acc = 0;
  const slices = items.map((d, i) => {
    const value = getValue(d) || 0;
    const color = colors[i % colors.length];
    if (value === 0) return { data: d, color, path: null, key: getKey(d) };
    const startAngle = (acc / total) * Math.PI * 2 - Math.PI / 2;
    acc += value;
    const endAngle   = (acc / total) * Math.PI * 2 - Math.PI / 2;
    const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle),   y2 = cy + r * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    return { path, color, data: d, key: getKey(d) };
  });

  const isDim = (key) =>
    (selectedKey && key !== selectedKey) ||
    (hover && hover !== key && !selectedKey);

  return (
    <div className="surface p-6" data-testid={testId}>
      <div className="mb-3">
        <h3 className="font-display text-base font-extrabold inline-flex items-center gap-1.5">
          {title}
          {header}
        </h3>
        {subtitle && <p className="text-[11px] text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-start gap-6 flex-wrap">
        <svg viewBox={`0 0 ${size} ${size}`} width="200" height="200" style={{ flexShrink: 0 }}>
          {slices.filter((s) => s.path).map((seg, i) => (
            <path
              key={i}
              d={seg.path}
              fill={seg.color}
              opacity={isDim(seg.key) ? 0.3 : 1}
              onMouseEnter={() => setHover(seg.key)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect && onSelect(seg.key)}
              data-testid={`${testId}-slice-${String(seg.key).replace(/\s+/g, '-').toLowerCase()}`}
              style={{ cursor: onSelect ? "pointer" : "default", transition: "opacity 0.15s ease" }}
            />
          ))}
          <circle cx={cx} cy={cy} r={r * 0.55} fill="#fff" />
          <text x={cx} y={cy - 2} textAnchor="middle" fontSize="22" fontWeight="800"
            fill="#111827" style={{ fontFamily: "Manrope, sans-serif" }}>
            {centerValue}
          </text>
          <text x={cx} y={cy + 14} textAnchor="middle" fontSize="9" fontWeight="700"
            fill="#94a3b8" style={{ letterSpacing: "0.1em" }}>
            {centerLabel}
          </text>
        </svg>
        <div
          className="flex-1"
          style={{ minWidth: 200, ...(legendMaxHeight ? { maxHeight: legendMaxHeight, overflowY: "auto" } : {}) }}
        >
          {slices.map((seg, i) => renderLegendRow({
            item: seg.data,
            color: seg.color,
            key: seg.key,
            index: i,
            total,
            isHover: hover === seg.key,
            isSelected: selectedKey === seg.key,
            isDim: isDim(seg.key),
            onEnter: () => setHover(seg.key),
            onLeave: () => setHover(null),
            onClick: () => onSelect && onSelect(seg.key),
          }))}
        </div>
      </div>
    </div>
  );
}
